import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import SceneCanvas from '../canvas/SceneCanvas';
import PlaybackBar from '../toolbar/PlaybackBar';
import { useSceneStore } from '../../stores/sceneStore';
import { getSpineManager } from '../../lib/spine-manager';
import { api } from '../../lib/api-client';
import type { Scene } from '@spine-workspace/shared';

export default function ViewerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [scene, setScene] = useState<Scene | null>(null);
  const [error, setError] = useState('');

  const { initScene, layers, selectedLayerId, selectLayer, toggleLayerVisibility } = useSceneStore();

  useEffect(() => {
    if (!id) return;
    api.get<{ scene: Scene }>(`/api/scenes/${id}`)
      .then(({ scene: s }) => {
        setScene(s);
        initScene(
          s.id, s.name, s.canvasWidth, s.canvasHeight,
          s.sceneData?.layers ?? [],
          s.sceneData?.canvas?.backgroundColor ?? '#000000'
        );
        const firstLayer = s.sceneData?.layers?.[0];
        if (firstLayer) selectLayer(firstLayer.id);
      })
      .catch(() => setError('You do not have access to this scene.'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-bg-deepest">
        <div className="text-center space-y-3">
          <p className="text-error text-lg">Unable to load scene</p>
          <p className="text-text-tertiary text-sm">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="font-sans text-sm text-accent hover:underline"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!scene) {
    return (
      <div className="flex items-center justify-center h-screen bg-bg-deepest text-text-tertiary text-sm font-sans">
        Loading…
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-bg-deepest">
      {/* Header */}
      <div className="bg-bg-surface border-b border-border-subtle px-4 py-2 flex items-center gap-3 shrink-0 h-[44px]">
        <span className="font-serif text-sm font-medium text-text-primary flex-1 truncate">{scene.name}</span>
        <span className="font-sans text-xs text-text-tertiary bg-bg-elevated border border-border px-2 py-0.5 rounded">View only</span>
      </div>

      {/* Main area: canvas + layer panel */}
      <div className="flex flex-1 overflow-hidden">
        {/* Canvas */}
        <div className="flex-1 overflow-hidden">
          <SceneCanvas
            canvasWidth={scene.canvasWidth}
            canvasHeight={scene.canvasHeight}
            layers={layers}
            readOnly
          />
        </div>

        {/* Layer visibility panel */}
        {layers.length > 0 && (
          <div className="w-[160px] shrink-0 bg-bg-surface border-l border-border-subtle flex flex-col">
            <div className="px-3 py-2 border-b border-border-subtle">
              <span className="font-sans text-xs font-medium text-text-secondary uppercase tracking-wide">Layers</span>
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {[...layers].reverse().map((layer) => (
                <div
                  key={layer.id}
                  onClick={() => selectLayer(layer.id === selectedLayerId ? null : layer.id)}
                  className={`flex items-center gap-2 mx-2 my-0.5 px-2 py-1.5 rounded-md cursor-pointer transition-all duration-fast ${
                    layer.id === selectedLayerId ? 'bg-accent-muted' : 'hover:bg-bg-hover'
                  }`}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleLayerVisibility(layer.id);
                      getSpineManager().setLayerVisible(layer.id, !layer.visible);
                    }}
                    className={`shrink-0 transition-colors duration-fast ${
                      layer.visible ? 'text-text-secondary hover:text-text-primary' : 'text-accent'
                    }`}
                  >
                    {layer.visible ? <Eye size={13} /> : <EyeSlash size={13} />}
                  </button>
                  <span className={`font-sans text-xs truncate ${layer.visible ? 'text-text-primary' : 'text-text-tertiary'}`}>
                    {layer.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Playback bar */}
      <PlaybackBar />
    </div>
  );
}
